import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getCurrentRegion } from './utils/region';

type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

// No hardcoded fallbacks: a missing variable must fail loudly at startup
// instead of silently pointing the app at a baked-in project.
const usFirebaseConfig: FirebaseWebConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

const US_ENV_VAR_BY_FIELD: Record<keyof FirebaseWebConfig, string> = {
  apiKey: 'REACT_APP_FIREBASE_API_KEY',
  authDomain: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
  projectId: 'REACT_APP_FIREBASE_PROJECT_ID',
  storageBucket: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'REACT_APP_FIREBASE_APP_ID',
};

const missingEnvVars = (Object.keys(usFirebaseConfig) as (keyof FirebaseWebConfig)[])
  .filter((field) => !usFirebaseConfig[field])
  .map((field) => US_ENV_VAR_BY_FIELD[field]);

if (missingEnvVars.length > 0) {
  throw new Error(
    `Missing required Firebase environment variables: ${missingEnvVars.join(', ')}. ` +
      'Set them in frontend/.env.development (local dev) or frontend/.env.production (builds).'
  );
}

// The CA project is optional; fields fall back to the US project values.
const caFirebaseConfig: FirebaseWebConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_CA_API_KEY || usFirebaseConfig.apiKey,
  authDomain: process.env.REACT_APP_FIREBASE_CA_AUTH_DOMAIN || usFirebaseConfig.authDomain,
  projectId: process.env.REACT_APP_FIREBASE_CA_PROJECT_ID || usFirebaseConfig.projectId,
  storageBucket: process.env.REACT_APP_FIREBASE_CA_STORAGE_BUCKET || usFirebaseConfig.storageBucket,
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_CA_MESSAGING_SENDER_ID || usFirebaseConfig.messagingSenderId,
  appId: process.env.REACT_APP_FIREBASE_CA_APP_ID || usFirebaseConfig.appId,
};

const region = getCurrentRegion();
const firebaseConfig = region === 'ca' ? caFirebaseConfig : usFirebaseConfig;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);

// Connect to emulators in development
if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_EMULATORS === 'true') {
  try {
    // Connect to Auth emulator
    if (!auth.emulatorConfig) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    }
    
    // Connect to Firestore emulator
    // Check if already connected by trying to connect (will throw if already connected)
    try {
      connectFirestoreEmulator(db, 'localhost', 8080);
    } catch (error) {
      // Already connected, ignore the error
      console.log('Firestore emulator already connected');
    }
    
    // Connect to Functions emulator
    if (!functions.customDomain) {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
    
    // Connect to Storage emulator
    try {
      connectStorageEmulator(storage, 'localhost', 9199);
    } catch (error) {
      console.log('Storage emulator already connected');
    }
    
    console.log('🔧 Connected to Firebase emulators (Auth, Firestore, Functions, Storage)');
  } catch (error) {
    console.log('⚠️ Emulator connection failed, using production:', error);
  }
} else {
  console.log('🌐 Using production Firebase services');
}

export const firebaseApp = app;
export default app;
